import {
  IFRAME_HEIGHT_MAX,
  IFRAME_HEIGHT_MIN,
  type IframeAspectRatio,
  iframeUrlProblem,
  isIframeAspectRatio,
  isValidIframeHeight,
  pickT,
} from "@repo/shared";
import { useEffect, useRef, useState } from "react";
import { isAppOrigin, SandboxedIframe } from "@/components/sandboxed-iframe";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { T } from "./iframe-editor.i18n";

type IframeEditorProps = {
  url: string;
  height: string;
  aspectRatio: "" | IframeAspectRatio;
  flowLanguage: string;
  onUrlChange: (url: string) => void;
  onHeightChange: (height: string) => void;
  onAspectRatioChange: (aspectRatio: "" | IframeAspectRatio) => void;
};

// Radix Select cannot hold an empty-string item value.
const FIXED = "fixed";
const PREVIEW_DEBOUNCE_MS = 500;

/**
 * QL-776 — URL in, sandboxed React `<iframe src>` out. The validation here is
 * UX only; `iframeUrlProblem` is the same rule the API service enforces.
 */
export function IframeEditor({
  url,
  height,
  aspectRatio,
  flowLanguage,
  onUrlChange,
  onHeightChange,
  onAspectRatioChange,
}: IframeEditorProps) {
  const t = pickT(T, flowLanguage);
  const trimmed = url.trim();
  const urlProblem = iframeUrlProblem(trimmed);
  const pointsAtApp = urlProblem === null && isAppOrigin(trimmed);
  const heightNumber = Number(height);
  // The preview loads a third-party page: a URL set from outside (the saved
  // component, or the dialog switching to another one) previews at once; only
  // typed edits wait until typing pauses.
  const typedRef = useRef(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    urlProblem === null && !pointsAtApp ? trimmed : null,
  );
  useEffect(() => {
    const typed = typedRef.current;
    typedRef.current = false;
    if (urlProblem !== null || pointsAtApp) {
      setPreviewUrl(null);
      return;
    }
    if (!typed) {
      setPreviewUrl(trimmed);
      return;
    }
    const timer = setTimeout(() => setPreviewUrl(trimmed), PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmed, urlProblem, pointsAtApp]);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="iframe-url">
          {t.url} <span className="text-destructive">*</span>
        </Label>
        <Input
          id="iframe-url"
          type="url"
          value={url}
          onChange={(e) => {
            typedRef.current = true;
            onUrlChange(e.target.value);
          }}
          placeholder={t.placeholder}
          required
        />
        {trimmed !== "" && (urlProblem !== null || pointsAtApp) ? (
          <p role="alert" className="text-xs text-destructive">
            {pointsAtApp ? t.urlAppOrigin : t.urlInvalid}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">{t.helpText}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="iframe-size">{t.size}</Label>
          <Select
            value={aspectRatio === "" ? FIXED : aspectRatio}
            onValueChange={(v) => onAspectRatioChange(isIframeAspectRatio(v) ? v : "")}
          >
            <SelectTrigger id="iframe-size">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FIXED}>{t.fixedHeight}</SelectItem>
              <SelectItem value="16:9">{t.ratio169}</SelectItem>
              <SelectItem value="4:3">{t.ratio43}</SelectItem>
              <SelectItem value="1:1">{t.ratio11}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {aspectRatio === "" && (
          <div className="space-y-2">
            <Label htmlFor="iframe-height">{t.heightPx}</Label>
            <Input
              id="iframe-height"
              type="number"
              min={IFRAME_HEIGHT_MIN}
              max={IFRAME_HEIGHT_MAX}
              value={height}
              onChange={(e) => onHeightChange(e.target.value)}
            />
          </div>
        )}
      </div>

      {previewUrl !== null && (
        <div className="space-y-2">
          <SandboxedIframe
            url={previewUrl}
            height={isValidIframeHeight(heightNumber) ? heightNumber : undefined}
            aspectRatio={aspectRatio === "" ? undefined : aspectRatio}
          />
          <p className="text-xs text-muted-foreground">{t.previewCaption}</p>
        </div>
      )}
    </div>
  );
}
